// =========================================================================
// IncidentsScene — per-surface projections. Fork-owned (zone C).
//
// Transformation layer: pure functions, no React, no fetches, no mutation.
//
// Source renderer: `renderIncident(id)` at line 2322 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`, reading the
// zero-argument arrows on each `INCIDENTS` record.
//
// The seed models every interpolated figure as a part list — `{kind:'text'}`
// and `{kind:'cost', field}` — because the demo's `kpis()` closure read live
// work-order fields out of `B`. Resolving those parts against the incident's
// named work order is this file's main job.
//
// @see docs/logic/issues/incidents.md
// =========================================================================

import type { SourceCalc } from '@/versions/v1/chrome-oem/SourceDrawerBody';
import { CALC_NOTE } from '@/versions/v1/shared/source-calc';
import {
  fmt$,
  INC_PLANNED,
  type IncidentCosts,
} from '@/versions/v1/shared/oem-engine';
import {
  DEFAULT_INCIDENT_ID,
  INCIDENTS,
  type Incident,
  type IncidentChart,
  type IncidentCostField,
  type IncidentKpi,
  type IncidentKpiValue,
  type IncidentSeriesSource,
  type IncidentTextPart,
} from '@/versions/v1/shared/seed/incidents';
import type { DailyMetricRow, TrendSeriesRegistry, WorkOrder } from '@/versions/v1/shared/useOemData';

// ---------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------

/**
 * resolveIncident(incidentId) → Incident
 * Projection rule: the demo's `INCIDENTS.find(i => i.id === id) || INCIDENTS[0]`.
 *   An unknown or absent id falls back to `DEFAULT_INCIDENT_ID`.
 */
export function resolveIncident(incidentId: string | undefined): Incident {
  const byId = incidentId ? INCIDENTS.find((i) => i.id === incidentId) : undefined;
  return byId ?? INCIDENTS.find((i) => i.id === DEFAULT_INCIDENT_ID) ?? INCIDENTS[0];
}

/** `true` when the id names an incident — drives the "not found" note. */
export function isKnownIncident(incidentId: string | undefined): boolean {
  return Boolean(incidentId) && INCIDENTS.some((i) => i.id === incidentId);
}

// ---------------------------------------------------------------------------
// Cost fields
// ---------------------------------------------------------------------------

/**
 * costField(workOrder, field) → number | null
 * Derive: `'all_in_cost_aud'` is the demo's `total_direct_cost_aud +
 *   downtime_cost_aud` and has no column of its own. `null` when the work
 *   order is not loaded yet.
 */
export function costField(
  workOrder: WorkOrder | undefined,
  field: IncidentCostField,
): number | null {
  if (!workOrder) return null;
  if (field === 'all_in_cost_aud') {
    return workOrder.total_direct_cost_aud + workOrder.downtime_cost_aud;
  }
  return workOrder[field];
}

/** The placeholder a money figure shows while its work order is still loading. */
export const COST_PENDING = '—';

/**
 * renderParts(parts, workOrder) → string
 * Projection rule: walk the seed's part list, keeping `'text'` verbatim and
 *   formatting `'cost'` with `fmt$()` off the incident's work order — which is
 *   exactly what the demo's template literal did inline.
 */
export function renderParts(
  parts: readonly IncidentTextPart[],
  workOrder: WorkOrder | undefined,
): string {
  return parts
    .map((part) => {
      if (part.kind === 'text') return part.text;
      const value = costField(workOrder, part.field);
      return value == null ? COST_PENDING : fmt$(value);
    })
    .join('');
}

/** A KPI headline, resolved to display text plus the demo's red emphasis flag. */
export interface KpiFigure {
  readonly text: string;
  /** `true` when the seed marked the value `emphasis: 'crit'`. */
  readonly critical: boolean;
}

/**
 * renderKpiValue(value, workOrder) → KpiFigure
 * Derive: a `'text'` value is a literal; a `'cost'` value is a work-order
 *   field. The `crit` emphasis is the Attribution tile's red — the point of
 *   that tile is that the cause was not the component.
 */
export function renderKpiValue(
  value: IncidentKpiValue,
  workOrder: WorkOrder | undefined,
): KpiFigure {
  if (value.kind === 'text') {
    return { text: value.text, critical: value.emphasis === 'crit' };
  }
  const resolved = costField(workOrder, value.field);
  return { text: resolved == null ? COST_PENDING : fmt$(resolved), critical: false };
}

/**
 * kpiCalc(kpi, workOrder) → SourceCalc | undefined
 * Projection rule: the demo's `FIG(…, 'wos', {wo_id}, {calc})` on the three
 *   All-in cost tiles (lines 2297, 2305, 2313). The rule is the one the machine
 *   history's work-order figure states (line 2201); the step is the tile's own
 *   calc line with this work order's figures, ending in the tile's value.
 *   `undefined` when the tile carries no calc note.
 */
export function kpiCalc(kpi: IncidentKpi, workOrder: WorkOrder | undefined): SourceCalc | undefined {
  if (!kpi.calc) return undefined;
  return {
    formula: CALC_NOTE.workOrderAllIn,
    steps: [`${renderParts(kpi.calc, workOrder)} = ${renderKpiValue(kpi.value, workOrder).text}`],
  };
}

// ---------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------

/** A chart series resolved to plotted values and their axis labels. */
export interface ResolvedSeries {
  readonly labels: readonly string[];
  readonly values: readonly (number | null)[];
}

/** What the chart needs from the telemetry domain. */
export interface SeriesContext {
  /** `useOemData('telemetry').daily_metric`, unpacked. */
  readonly dailyMetric: readonly DailyMetricRow[];
  /** `useOemData('telemetry').incident_series`. */
  readonly incidentSeries: TrendSeriesRegistry;
}

/**
 * resolveSeries(source, ctx) → ResolvedSeries
 *
 * Projection rule: the two sources are read exactly as the demo read them.
 *   - `dailyMetric`    → `DM.filter(d => d.machine_id === machine)`, plotting
 *     `.value` against `.date`. The file carries one metric per machine, so no
 *     metric filter is needed — and adding one would silently empty the chart
 *     if a machine's metric were ever renamed.
 *   - `incidentSeries` → `B.incident_series[machine].rows`, plotting `row[1]`
 *     against `row[0]`.
 * Derive: labels are `date.slice(5)` (`MM-DD`) in both cases.
 * Invariant: a `null` value is carried through, not coerced — both incident
 *   series end on one, and {@link LineChart} breaks the line there.
 */
export function resolveSeries(
  source: IncidentSeriesSource,
  ctx: SeriesContext,
): ResolvedSeries {
  if (source.kind === 'dailyMetric') {
    const rows = ctx.dailyMetric.filter((row) => row.machine_id === source.machine);
    return {
      labels: rows.map((row) => row.date.slice(5)),
      values: rows.map((row) => row.value),
    };
  }
  const series = ctx.incidentSeries[source.machine];
  if (!series) return { labels: [], values: [] };
  return {
    labels: series.rows.map(([date]) => date.slice(5)),
    values: series.rows.map(([, value]) => value),
  };
}

/**
 * chartLabels(chart, ctx) → string[]
 * Projection rule: every series on an incident chart shares one x axis; the
 *   demo took the labels off the first series, so this does too.
 */
export function chartLabels(chart: IncidentChart, ctx: SeriesContext): readonly string[] {
  const first = chart.series[0];
  return first ? resolveSeries(first.source, ctx).labels : [];
}

// ---------------------------------------------------------------------------
// Actual against planned
// ---------------------------------------------------------------------------

/** The "as it happened" / "replayed" cost pair, scaled for the bar chart. */
export interface CostComparison {
  /** All-in cost off the work order, `0` while it loads. */
  readonly actual: number;
  /** `INC_PLANNED[id]` — what the close-out priced the planned job at. */
  readonly planned: number;
  readonly actualPct: number;
  readonly plannedPct: number;
  /** `actual - planned` — what the failure cost over the planned job. */
  readonly delta: number;
}

/**
 * costComparison(incident, costs) → CostComparison
 * Derive: both bars are scaled against the **actual**, so the planned bar is
 *   always the short one — the demo's framing, kept, because the point of the
 *   pair is how much longer the red bar is.
 */
export function costComparison(incident: Incident, costs: IncidentCosts): CostComparison {
  const actual = costs.allIn ?? 0;
  const planned = INC_PLANNED[incident.id] ?? 0;
  const denominator = Math.max(actual, planned, 1);
  return {
    actual,
    planned,
    actualPct: Math.round((actual / denominator) * 100),
    plannedPct: Math.round((planned / denominator) * 100),
    delta: actual - planned,
  };
}

// ---------------------------------------------------------------------------
// Timelines
// ---------------------------------------------------------------------------

/**
 * documentHref(openDoc) → string
 * Projection rule: the demo's inline `openDoc('eml:01')` buttons opened a lake
 *   file in the drawer. The port has one place that opens a file — the data
 *   browser — so every document link routes there with the key in `?doc=`.
 */
export function documentHref(openDoc: string): string {
  return `/integration/data?doc=${encodeURIComponent(openDoc)}`;
}

/**
 * momentKey(when, index) → string
 * Invariant: two moments can share a `when` (`'04–06 Sep'` repeats across
 *   incidents and `'Aug'` twice within one), so the index is part of the key.
 *   The list is static seed data, so the index is stable.
 */
export function momentKey(when: string, index: number): string {
  return `${index}::${when}`;
}
